<template>
  <div id="top">
    <SiteNav @emergency="openSos('nav')" />

    <!-- ============ 首屏 ============ -->
    <section class="hero bg-grid">
      <div class="hero__glow"></div>
      <div class="wrap hero__grid">
        <div class="hero__copy">
          <p class="hero__status">
            <span class="live-dot"></span>
            <span>{{ c.hero.status }}</span>
            <span class="sep">/</span>
            <span>{{ c.hero.clock }} {{ clock }}</span>
          </p>
          <p class="eyebrow">{{ c.hero.eyebrow }}</p>
          <h1 class="hero__title">
            <span class="line">{{ c.hero.titleA }}</span>
            <span class="line">{{ c.hero.titleB }}<em>{{ c.hero.titleC }}</em></span>
          </h1>
          <p class="hero__sub">{{ c.hero.sub }}</p>
          <div class="hero__ctas">
            <a href="#contact" class="btn btn--primary" @click="track('click_cta', { label: 'hero' })">{{ c.hero.cta1 }}</a>
            <button class="btn btn--ghost" @click="openSos('hero')">{{ c.hero.cta2 }}</button>
          </div>

          <div class="telemetry">
            <p class="telemetry__label"><span class="dot-accent"></span>{{ c.hero.telemetry }}</p>
            <div class="telemetry__row">
              <div v-for="(s, i) in c.hero.stats" :key="i" class="telemetry__item">
                <strong>{{ s.live ? liveCount : s.value }}</strong>
                <span>{{ s.live ? s.label.replace('{loss}', liveLoss) : s.label }}</span>
              </div>
            </div>
          </div>
        </div>
        <RadarFeed class="hero__radar" />
      </div>
    </section>

    <!-- ============ 滚动条 ============ -->
    <div class="ticker" aria-hidden="true">
      <div class="ticker__track">
        <template v-for="n in 2" :key="n">
          <span v-for="(t, i) in c.ticker" :key="n + '-' + i" class="ticker__item">{{ t }}<b>///</b></span>
        </template>
      </div>
    </div>

    <!-- ============ 为什么 ============ -->
    <section id="why" class="section section--paper">
      <div class="wrap">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ c.why.eyebrow }}</p>
          <h2 class="h-section">{{ c.why.title }}</h2>
          <p class="lead">{{ c.why.sub }}</p>
        </div>
        <article v-for="it in c.why.incidents.filter(x => x.featured)" :key="it.name" class="incident-hero" v-reveal>
          <div class="incident-hero__main">
            <p class="incident-hero__tag"><span class="incident-hero__flag">{{ c.why.latest }}</span><span class="tag-mono">{{ it.date }}</span></p>
            <h3 class="incident-hero__name">{{ it.name }} <strong>{{ it.loss }}</strong></h3>
            <p class="incident-hero__how">{{ it.how }}</p>
          </div>
          <ol class="chain">
            <li v-for="(step, j) in it.chain" :key="step"><span>0{{ j + 1 }}</span>{{ step }}</li>
          </ol>
        </article>
        <div class="grid-3">
          <article v-for="(it, i) in c.why.incidents.filter(x => !x.featured)" :key="it.name" class="card incident" v-reveal :class="'reveal-d' + (i + 1)">
            <div class="incident__top">
              <span class="tag-mono">{{ it.date }}</span>
              <span class="incident__loss">{{ it.loss }}</span>
            </div>
            <h3 class="card__title">{{ it.name }}</h3>
            <p class="card__text">{{ it.how }}</p>
          </article>
        </div>
        <p class="footnote">{{ c.why.note }}</p>
      </div>
    </section>

    <!-- ============ 防护范围 ============ -->
    <section id="coverage" class="section section--paper">
      <div class="wrap">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ c.coverage.eyebrow }}</p>
          <h2 class="h-section">{{ c.coverage.title }}</h2>
          <p class="lead">{{ c.coverage.sub }}</p>
        </div>
        <div class="grid-3">
          <article v-for="(it, i) in c.coverage.items" :key="it.tag" class="card layer" v-reveal :class="'reveal-d' + ((i % 3) + 1)">
            <div class="layer__top">
              <span class="layer__icon" v-html="icons[i]"></span>
              <span class="tag-mono">{{ it.tag }}</span>
            </div>
            <h3 class="card__title">{{ it.title }}</h3>
            <ul class="checks">
              <li v-for="p in it.points" :key="p">{{ p }}</li>
            </ul>
          </article>
        </div>
      </div>
    </section>

    <!-- ============ 客户案例（有真实素材才显示） ============ -->
    <section v-if="CASES.length" id="cases" class="section section--paper">
      <div class="wrap">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ c.cases.eyebrow }}</p>
          <h2 class="h-section">{{ c.cases.title }}</h2>
          <p class="lead">{{ c.cases.sub }}</p>
        </div>
        <div class="cases" :class="{ 'cases--one': CASES.length === 1 }">
          <article v-for="(cs, i) in CASES" :key="i" class="card case" v-reveal>
            <p class="tag-mono">CASE // 0{{ i + 1 }} · {{ cs[lang].tag }}</p>
            <h3 class="card__title">{{ cs[lang].client }}</h3>
            <dl class="case__dl">
              <div><dt>{{ c.cases.problem }}</dt><dd>{{ cs[lang].problem }}</dd></div>
              <div><dt>{{ c.cases.did }}</dt><dd>{{ cs[lang].did }}</dd></div>
              <div><dt>{{ c.cases.result }}</dt><dd>{{ cs[lang].result }}</dd></div>
            </dl>
          </article>
        </div>
      </div>
    </section>

    <!-- ============ 适合这些团队 + 价格入口（完整内容在 /solutions、/pricing） ============ -->
    <section id="plans" class="section section--dark bg-grid bg-glow">
      <div class="wrap rel">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ ht.whoEyebrow }}</p>
          <h2 class="h-section">{{ ht.whoTitle }}</h2>
          <p class="lead">{{ ht.whoSub }}</p>
        </div>
        <div class="who">
          <router-link v-for="(s, i) in SOLUTIONS" :key="s.id" :to="localePath('/solutions/') + '#' + s.id" class="who__item" v-reveal :class="'reveal-d' + ((i % 4) + 1)">
            <span class="who__no">{{ String(i + 1).padStart(2, '0') }}</span>
            <strong class="who__name">{{ s[lang].name }}</strong>
            <span class="who__desc">{{ s[lang].who }}</span>
            <span class="who__arrow">→</span>
          </router-link>
        </div>
        <p class="more-link"><router-link :to="localePath('/solutions/')">{{ ht.whoAll }} <span class="arr">→</span></router-link></p>

        <div class="head-center teaser-head" v-reveal>
          <p class="eyebrow">{{ ht.priceEyebrow }}</p>
          <h2 class="h-section">{{ ht.priceTitle }}</h2>
          <p class="lead">{{ ht.priceSub }}</p>
        </div>
        <div class="teaser">
          <article v-for="(e, i) in teaserItems" :key="e.id" class="teaser__card" :class="{ 'teaser__card--free': i === 0 }" v-reveal>
            <h3 class="teaser__name">{{ e.name }}</h3>
            <p class="teaser__price"><strong>{{ e.price }}</strong><span>{{ e.unit }}</span></p>
            <p class="teaser__desc">{{ e.desc }}</p>
            <a v-if="i === 0" href="#contact" class="btn btn--primary" @click="pick(e.name, 'teaser_' + e.id)">{{ c.plans.free.cta }}</a>
            <router-link v-else :to="e.to" class="btn btn--ghost">{{ c.pricingPage.more }}</router-link>
          </article>
        </div>
        <p class="more-link more-link--solid"><router-link :to="localePath('/pricing/')" @click="track('click_cta', { label: 'home_pricing' })">{{ ht.priceAll }} <span class="arr">→</span></router-link></p>
      </div>
    </section>

    <!-- ============ 合作流程 ============ -->
    <section id="process" class="section section--paper">
      <div class="wrap">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ c.process.eyebrow }}</p>
          <h2 class="h-section">{{ c.process.title }}</h2>
        </div>
        <ol class="steps">
          <li v-for="(s, i) in c.process.steps" :key="s.n" class="step" v-reveal :class="'reveal-d' + (i + 1)">
            <span class="step__n">{{ s.n }}</span>
            <h3 class="card__title">{{ s.title }}</h3>
            <p class="card__text">{{ s.text }}</p>
          </li>
        </ol>
      </div>
    </section>

    <!-- ============ 黑U检测 ============ -->
    <section id="tools" class="section section--dark bg-grid">
      <div class="wrap rel">
        <div class="head-center" v-reveal>
          <p class="eyebrow">{{ c.tools.eyebrow }}</p>
          <h2 class="h-section">{{ c.tools.title }}</h2>
          <p class="lead">{{ c.tools.sub }}</p>
        </div>
        <BlackUChecker v-reveal />
        <FreezeList />
        <p class="more-link"><router-link :to="localePath('/tools/usdt-check/')">{{ lang === 'zh' ? '黑U是什么、收到黑U怎么办' : 'What tainted USDT is and what to do' }} <span class="arr">→</span></router-link></p>
      </div>
    </section>

    <!-- ============ 关于我们 ============ -->
    <section id="about" class="section section--paper">
      <div class="wrap about">
        <div v-reveal>
          <p class="eyebrow">{{ c.about.eyebrow }}</p>
          <h2 class="h-section">{{ c.about.titleA }}<br>{{ c.about.titleB }}</h2>
          <p class="about__text">{{ c.about.text }}</p>
          <div class="about__tags">
            <span v-for="t in c.about.tags" :key="t">{{ t }}</span>
          </div>
          <p class="about__verify">{{ c.about.verify }} <a :href="'mailto:' + CONTACT_EMAIL">{{ CONTACT_EMAIL }}</a></p>
        </div>
        <div class="about__card card" v-reveal>
          <p class="about__est"><span>{{ c.about.since }}</span><strong>2019</strong></p>
          <dl class="about__facts">
            <div v-for="f in c.about.facts" :key="f.k"><dt>{{ f.k }}</dt><dd>{{ f.v }}</dd></div>
          </dl>
        </div>
      </div>
    </section>

    <CtaBlock @emergency="openSos('cta')" />
    <ContactSection source="home" />
    <SiteFooter />

    <EmergencyModal :open="sos" @close="sos = false" />
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { content as c, lang, localePath, CONTACT_EMAIL } from '@/i18n'
import { CASES } from '@/content-pages.mjs'
import { SOLUTIONS, PRICING } from '@/content-solutions.mjs'
import { track } from '@/track'
import { setIntent } from '@/leadIntent'
import { fetchIncidents, formatUsd } from '@/incidents'
import SiteNav from '@/components/SiteNav.vue'
import RadarFeed from '@/components/RadarFeed.vue'
import BlackUChecker from '@/components/BlackUChecker.vue'
import FreezeList from '@/components/FreezeList.vue'
import ContactSection from '@/components/ContactSection.vue'
import CtaBlock from '@/components/CtaBlock.vue'
import SiteFooter from '@/components/SiteFooter.vue'
import EmergencyModal from '@/components/EmergencyModal.vue'

const route = useRoute()
const sos = ref(false)
function openSos(where) { sos.value = true; track('click_emergency', { label: where }) }
function pick(name, label) { setIntent(name); track('click_cta', { label }) }

// 北京时间时钟
const clock = ref('')
function tick() {
  clock.value = new Date().toLocaleTimeString('en-GB', { timeZone: 'Asia/Shanghai', hour12: false })
}

// 过去 30 天区块链安全事件数与损失金额（DefiLlama，经 /api/incidents 缓存）
const liveCount = ref('—')
const lossValue = ref(null)
const liveLoss = computed(() => lossValue.value === null ? '—' : formatUsd(lossValue.value, lang.value))
// TronGrid 免费接口有频率限制，失败后隔几秒重试
async function loadCount(attempt = 0) {
  try {
    const data = await fetchIncidents(attempt > 0)
    if (data.stats) {
      liveCount.value = String(data.stats.count30d)
      lossValue.value = data.stats.loss30d
    }
  } catch (e) {
    if (attempt < 3) setTimeout(() => loadCount(attempt + 1), 4000 * (attempt + 1))
  }
}

// 首页只放价格入口，完整对比在 /pricing
const ht = computed(() => c.value.homeTeaser)
const teaserItems = computed(() => {
  const l = lang.value
  const [consult, assess] = PRICING.entry
  return [
    { id: consult.id, ...consult[l] },
    { id: assess.id, ...assess[l], to: localePath('/services/' + assess.slug + '/') },
    { id: 'partner', name: ht.value.subName, price: ht.value.subPrice, unit: ht.value.subUnit, desc: ht.value.subDesc, to: localePath('/pricing/') + '#plan-basic' },
  ]
})

// 旧链接兼容：/home?page=N、/about、/service 等跳到对应区块
const LEGACY = ['top', 'coverage', 'coverage', 'about', 'about', 'contact', 'about', 'tools']
function jumpToHash() {
  const hash = route.hash ? route.hash.slice(1) : (route.query.page !== undefined ? LEGACY[+route.query.page] : null)
  if (!hash) return
  // 用 instant：html 设置了平滑滚动，页面刚加载时平滑滚动会被字体/图片加载打断而停在半路
  const go = () => {
    const el = document.getElementById(hash)
    if (el) el.scrollIntoView({ behavior: 'instant', block: 'start' })
  }
  requestAnimationFrame(go)
  if (document.readyState !== 'complete') window.addEventListener('load', go, { once: true })
}

let clockTimer, countTimer
onMounted(() => {
  tick(); clockTimer = setInterval(tick, 1000)
  loadCount(); countTimer = setInterval(() => loadCount(1), 15 * 60 * 1000)
  jumpToHash()
})
onUnmounted(() => { clearInterval(clockTimer); clearInterval(countTimer) })

// 六层防护图标（线条风格，跟随文字颜色）
const svg = p => `<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">${p}</svg>`
const icons = [
  svg('<rect x="3" y="4" width="18" height="12" rx="1.5"/><path d="M8 20h8M12 16v4"/>'),
  svg('<rect x="3" y="3" width="18" height="7" rx="1.5"/><rect x="3" y="14" width="18" height="7" rx="1.5"/><path d="M7 6.5h.01M7 17.5h.01"/>'),
  svg('<path d="M3 7h15a3 3 0 0 1 3 3v7a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7z"/><path d="M3 7l12-3v3M16 13.5h.01"/>'),
  svg('<path d="M12 3l8 4.5v9L12 21l-8-4.5v-9L12 3z"/><path d="M12 12l8-4.5M12 12v9M12 12L4 7.5"/>'),
  svg('<circle cx="6" cy="6" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="12" cy="18" r="2.5"/><path d="M8 7.5l3 8M16 7.5l-3 8M8.5 6h7"/>'),
  svg('<path d="M12 3a9 9 0 1 0 9 9"/><path d="M12 7v5l3 2"/><path d="M17 3l4 4M21 3l-4 4"/>'),
]
</script>

<style scoped>
.rel { position: relative; z-index: 1; }

/* ---------- 首屏 ---------- */
.hero { position: relative; min-height: 100vh; padding: calc(var(--nav-h) + 72px) 0 88px; overflow: hidden; background: var(--ink); }
.hero__glow { position: absolute; inset: 0; pointer-events: none;
  background:
    radial-gradient(600px 400px at 12% 20%, rgba(247,146,16,.10), transparent 70%),
    radial-gradient(700px 500px at 88% 75%, rgba(247,146,16,.12), transparent 70%); }
.hero__grid { position: relative; z-index: 1; display: grid; grid-template-columns: 1.1fr 0.9fr; gap: 56px; align-items: start; }
.hero__copy { padding-top: 64px; }
.hero__status { display: flex; align-items: center; gap: 10px; font-family: var(--font-mono); font-size: 11px; letter-spacing: .2em; color: var(--ink-muted); margin-bottom: 28px; }
.hero__status .sep { color: var(--ink-dim); }
.hero__title { font-family: var(--font-display); font-weight: 700; font-size: clamp(40px, 5.4vw, 72px); line-height: 1.08; letter-spacing: -0.02em; }
.hero__title .line { display: block; }
.hero__title em { font-style: normal; color: var(--accent); white-space: nowrap; }
.hero__sub { margin-top: 26px; font-size: 18px; color: var(--ink-muted); max-width: 580px; }
.hero__ctas { display: flex; flex-wrap: wrap; gap: 14px; margin-top: 36px; }

.telemetry { margin-top: 56px; padding-top: 22px; border-top: 1px solid var(--ink-line); max-width: 620px; }
.telemetry__label { font-family: var(--font-mono); font-size: 11px; letter-spacing: .24em; color: var(--accent); margin-bottom: 14px; }
.dot-accent { display: inline-block; width: 6px; height: 6px; border-radius: 50%; background: var(--accent); margin-right: 10px; vertical-align: middle; }
.telemetry__row { display: grid; grid-template-columns: repeat(3, 1fr); }
.telemetry__item { padding-right: 16px; }
.telemetry__item + .telemetry__item { padding-left: 20px; border-left: 1px solid var(--ink-line); }
.telemetry__item strong { display: block; font-family: var(--font-display); font-size: 34px; line-height: 1.1; min-height: 1.1em; color: var(--accent); }
.telemetry__item span { display: block; margin-top: 6px; font-size: 13px; color: var(--ink-muted); line-height: 1.45; }

/* ---------- 滚动条 ---------- */
.ticker { overflow: hidden; background: var(--ink-2); border-top: 1px solid var(--ink-line); border-bottom: 1px solid var(--ink-line); }
.ticker__track { display: flex; width: max-content; animation: marquee 40s linear infinite; }
.ticker__item { font-family: var(--font-mono); font-size: 12px; letter-spacing: .22em; color: var(--ink-muted); padding: 16px 0; white-space: nowrap; }
.ticker__item b { font-weight: 400; color: var(--accent); margin: 0 28px; }
@keyframes marquee { to { transform: translateX(-50%); } }

/* ---------- 通用卡片网格 ---------- */
.grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.card__title { font-family: var(--font-display); font-size: 21px; font-weight: 700; margin: 14px 0 10px; color: var(--paper-text); }
.card__text { color: var(--paper-muted); font-size: 15px; }
.footnote { margin-top: 24px; font-size: 12px; color: var(--paper-muted); text-align: center; }
.footnote--dark { color: var(--ink-dim); }

.incident__top { display: flex; justify-content: space-between; align-items: baseline; }
.incident__loss { font-family: var(--font-display); font-size: 26px; font-weight: 700; color: var(--accent); }
.incident-hero { display: grid; grid-template-columns: 1.15fr .85fr; gap: 48px; align-items: center; margin-bottom: 20px; padding: 44px 48px; background: var(--ink); color: var(--ink-text); border-left: 4px solid var(--accent); }
.incident-hero__tag { display: flex; align-items: center; gap: 14px; }
.incident-hero__tag .tag-mono { color: var(--ink-dim); }
.incident-hero__flag { background: var(--danger); color: #fff; font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; padding: 4px 10px; }
.incident-hero__name { margin-top: 16px; font-family: var(--font-display); font-size: 40px; line-height: 1.15; }
.incident-hero__name strong { color: var(--accent); margin-left: 10px; }
.incident-hero__how { margin-top: 16px; color: var(--ink-muted); font-size: 16px; line-height: 1.75; }
.chain { list-style: none; display: grid; gap: 0; counter-reset: c; }
.chain li { position: relative; display: flex; gap: 14px; align-items: baseline; padding: 14px 0 14px 18px; border-left: 1px dashed rgba(247,146,16,.5); font-size: 15px; color: var(--ink-text); }
.chain li::before { content: ''; position: absolute; left: -5px; top: 20px; width: 9px; height: 9px; background: var(--accent); }
.chain li:last-child::before { background: var(--danger); }
.chain li:last-child { color: #ff8a80; }
.chain span { font-family: var(--font-mono); font-size: 11px; letter-spacing: .1em; color: var(--accent); flex: none; }

.layer__top { display: flex; justify-content: space-between; align-items: center; }
.layer__icon { width: 44px; height: 44px; display: grid; place-items: center; border-radius: 50%; background: var(--accent-soft); color: var(--accent); }
.checks { display: grid; gap: 10px; margin-top: 6px; }
.checks li { position: relative; padding-left: 24px; font-size: 15px; color: var(--paper-muted); }
.checks li::before { content: ''; position: absolute; left: 2px; top: 8px; width: 10px; height: 6px; border-left: 2px solid var(--accent); border-bottom: 2px solid var(--accent); transform: rotate(-45deg); }
.checks--dark li { color: var(--ink-muted); }

/* ---------- 适合这些团队 / 价格入口 ---------- */
.who { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
.who__item { position: relative; display: flex; flex-direction: column; gap: 8px; padding: 24px 22px 44px; border: 1px solid var(--ink-line); background: rgba(255,255,255,.02); transition: border-color .25s, transform .25s, background .25s; }
.who__item:hover { border-color: rgba(247,146,16,.5); background: rgba(247,146,16,.06); transform: translateY(-3px); }
.who__no { font-family: var(--font-mono); font-size: 11px; letter-spacing: .14em; color: var(--accent); }
.who__name { font-family: var(--font-display); font-size: 19px; color: var(--ink-text); }
.who__desc { font-size: 13px; color: var(--ink-muted); line-height: 1.55; }
.who__arrow { position: absolute; right: 20px; bottom: 16px; color: var(--accent); transition: transform .25s; }
.who__item:hover .who__arrow { transform: translateX(4px); }
.more-link { margin-top: 40px; text-align: center; }
.more-link a { display: inline-flex; align-items: center; gap: 12px; padding: 18px 36px; font-size: 18px; font-weight: 600; letter-spacing: .02em; color: var(--accent); border: 1.5px solid var(--accent); background: rgba(247,146,16,.08); transition: background .2s, color .2s, transform .2s; }
.more-link a:hover { background: var(--accent); color: #140a00; transform: translateY(-2px); }
.more-link--solid a { background: var(--accent); color: #140a00; box-shadow: 0 10px 30px rgba(247,146,16,.25); }
.more-link--solid a:hover { background: var(--accent-hot, #ffa940); }
.more-link .arr { font-size: 22px; line-height: 1; transition: transform .2s; }
.more-link a:hover .arr { transform: translateX(4px); }
.teaser-head { margin-top: 112px; }
.teaser { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
.teaser__card { display: flex; flex-direction: column; padding: 30px; border: 1px solid var(--ink-line); background: rgba(255,255,255,.025); }
.teaser__card--free { border: 1px dashed rgba(247,146,16,.6); background: rgba(247,146,16,.06); }
.teaser__name { font-family: var(--font-display); font-size: 22px; }
.teaser__price { margin: 12px 0 10px; display: flex; align-items: baseline; gap: 10px; }
.teaser__price strong { font-family: var(--font-display); font-size: 38px; color: var(--accent); line-height: 1.1; }
.teaser__price span { font-family: var(--font-mono); font-size: 12px; color: var(--ink-dim); }
.teaser__desc { color: var(--ink-muted); font-size: 15px; margin-bottom: 24px; }
.teaser__card .btn { margin-top: auto; }

/* ---------- 流程 ---------- */
.steps { list-style: none; display: grid; grid-template-columns: repeat(4, 1fr); gap: 0; border-top: 1px solid var(--paper-line); }
.step { position: relative; padding: 36px 28px 8px 0; }
.step + .step { padding-left: 28px; border-left: 1px solid var(--paper-line); }
.step::before { content: ''; position: absolute; top: -4px; left: 0; width: 7px; height: 7px; background: var(--accent); }
.step + .step::before { left: 28px; }
.step__n { font-family: var(--font-display); font-size: 44px; font-weight: 700; color: transparent; -webkit-text-stroke: 1px var(--accent); }

/* ---------- 关于 ---------- */
.about { display: grid; grid-template-columns: 1.2fr .8fr; gap: 64px; align-items: center; }
.about__text { margin-top: 24px; color: var(--paper-muted); font-size: 16px; max-width: 620px; }
.about__tags { display: flex; flex-wrap: wrap; gap: 10px; margin-top: 28px; }
.about__tags span { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; padding: 8px 12px; border: 1px solid var(--paper-line); color: var(--paper-text); }
.about__verify { margin-top: 22px; font-size: 14px; color: var(--paper-muted); }
.about__verify a { color: var(--accent); }
.about__card { padding: 40px; }
.about__est { font-family: var(--font-display); line-height: .95; }
.about__est span { display: block; font-size: 72px; color: transparent; -webkit-text-stroke: 1.5px var(--accent); }
.about__est strong { display: block; font-size: 96px; color: var(--accent); }
.about__facts { margin: 32px 0 0; display: grid; grid-template-columns: 1fr 1fr; gap: 0; border-top: 1px solid var(--paper-line); }
.about__facts div { padding: 16px 0; border-bottom: 1px solid var(--paper-line); }
.about__facts dt { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; color: var(--paper-muted); }
.about__facts dd { margin: 4px 0 0; font-family: var(--font-display); font-weight: 700; font-size: 16px; }

/* ---------- 案例 ---------- */
.cases { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
.case__dl { margin: 8px 0 0; display: grid; gap: 14px; }
.case__dl dt { font-family: var(--font-mono); font-size: 11px; letter-spacing: .16em; color: var(--accent); text-transform: uppercase; }
.case__dl dd { margin: 4px 0 0; color: var(--paper-muted); font-size: 15px; }
.cases--one { grid-template-columns: 1fr; max-width: 980px; margin: 0 auto; }
.cases--one .case { padding: 40px; }
.cases--one .case__dl { grid-template-columns: repeat(3, 1fr); gap: 28px; margin-top: 18px; }
@media (max-width: 900px) { .cases--one .case__dl { grid-template-columns: 1fr; gap: 16px; } .cases--one .case { padding: 24px; } }

/* ---------- 响应式 ---------- */
@media (max-width: 1080px) {
  .hero__grid { grid-template-columns: 1fr; gap: 64px; }
  .hero__copy { padding-top: 0; }
  .who { grid-template-columns: repeat(2, 1fr); }
  .teaser { grid-template-columns: 1fr; max-width: 640px; margin: 0 auto; }
}
@media (max-width: 900px) {
  .grid-3 { grid-template-columns: 1fr 1fr; }
  .incident-hero { grid-template-columns: 1fr; gap: 28px; padding: 32px 28px; }
  .steps { grid-template-columns: 1fr 1fr; }
  .step:nth-child(3) { padding-left: 0; border-left: 0; }
  .step:nth-child(3)::before { left: 0; }
  .about { grid-template-columns: 1fr; gap: 40px; }
  .cases { grid-template-columns: 1fr; }
}
@media (max-width: 600px) {
  .hero { padding-top: calc(var(--nav-h) + 40px); min-height: auto; }
  .hero__sub { font-size: 16px; }
  .telemetry__row { grid-template-columns: 1fr; gap: 16px; }
  .telemetry__item + .telemetry__item { padding-left: 0; border-left: 0; padding-top: 16px; border-top: 1px solid var(--ink-line); }
  .grid-3, .steps { grid-template-columns: 1fr; }
  .who { gap: 10px; }
  .who__item { padding: 18px 16px 36px; }
  .who__desc { display: none; }
  .teaser-head { margin-top: 80px; }
  .step, .step + .step { padding: 28px 0 8px; border-left: 0; }
  .step + .step::before { left: 0; }
  .teaser__card { padding: 24px; }
  .incident-hero { padding: 26px 20px; }
  .incident-hero__name { font-size: 30px; }
  .incident-hero__name strong { display: block; margin: 4px 0 0; }
  .more-link a { width: 100%; justify-content: center; padding: 16px 20px; font-size: 16px; }
  .about__est span { font-size: 52px; } .about__est strong { font-size: 72px; }
  .about__card { padding: 24px; }
}
</style>
